import React, { useState } from 'react';
import { FaEdit, FaChartBar, FaCheckCircle, FaTimesCircle, FaChevronDown, FaChevronUp, FaExternalLinkAlt, FaTrash } from 'react-icons/fa';
import KillSwitchButton from './KillSwitchButton';

const TIER_LABEL = {
  0: { text: 'Pulsuz',  color: '#888',    bg: '#f5f5f5' },
  1: { text: 'Starter', color: '#667eea', bg: '#eef0ff' },
  2: { text: 'Growth',  color: '#f39c12', bg: '#FEF9E7' },
  3: { text: 'Premium', color: '#8e44ad', bg: '#f5eeff' },
};

const AUDIENCE_MAP = {
  local:     '🏠 Yerli',
  tourist:   '✈️ Turist',
  corporate: '🏢 Korporativ',
};

const BTYPE_ICON = {
  restaurant: '🍽',
  cafe:       '☕',
  bar:        '🍺',
  fast_food:  '🍔',
  club:       '🎵',
  hookah:     '💨',
  teahouse:   '🍵',
  hotel:      '🏨',
  bakery:     '🥐',
};

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A2E' }}>{value}</div>
    </div>
  );
}

function SectionHead({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: '#FF6B35',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      marginBottom: 10, marginTop: 4,
      borderBottom: '1px solid #FFF0EB', paddingBottom: 4,
    }}>
      {children}
    </div>
  );
}

function ExpandedDetails({ business }) {
  const reg = business.reg_data;
  const tier = TIER_LABEL[business.premium_tier] || TIER_LABEL[0];

  return (
    <tr>
      <td colSpan={9} style={{ padding: 0, background: '#FAFAFA', borderBottom: '2px solid #FF6B35' }}>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 32px' }}>

            {/* Biznes məlumatları */}
            <div>
              <SectionHead>🏢 Biznes məlumatları</SectionHead>
              <DetailRow label="Ad" value={business.name} />
              <DetailRow label="Hüquqi Ad" value={reg?.legal_name} />
              <DetailRow label="VÖEN" value={reg?.voen} />
              <DetailRow label="Növ" value={`${BTYPE_ICON[business.business_type] || ''} ${business.business_type || '—'}`} />
              <DetailRow label="Mətbəx" value={business.cuisine_type || reg?.cuisine_type} />
              <DetailRow label="Tutum" value={business.total_capacity ? business.total_capacity + ' nəfər' : null} />
              <DetailRow label="Cari doluluq" value={business.current_occupancy != null ? business.current_occupancy + ' nəfər' : null} />
              <DetailRow label="Təsis ili" value={reg?.founded_year} />
              <DetailRow label="Lisenziya №" value={reg?.license_number} />
              <DetailRow label="Premium Tier" value={tier.text} />
              {business.subscription_end && (
                <DetailRow label="Abunə bitmə" value={new Date(business.subscription_end).toLocaleDateString('az-AZ')} />
              )}
              {reg?.target_audience?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Hədəf auditoriya</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {reg.target_audience.map(a => (
                      <span key={a} style={{ fontSize: 11, background: '#eef0ff', color: '#667eea',
                        padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                        {AUDIENCE_MAP[a] || a}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Ünvan & Əlaqə */}
            <div>
              <SectionHead>📍 Ünvan & Əlaqə</SectionHead>
              <DetailRow label="E-poçt" value={business.email} />
              <DetailRow label="Telefon" value={business.phone} />
              <DetailRow label="Vebsayt" value={reg?.website} />
              <DetailRow label="Ünvan" value={business.address || reg?.address} />
              <DetailRow label="Şəhər" value={business.city} />
              <DetailRow label="Poçt kodu" value={reg?.postal_code} />
              {business.latitude && business.longitude && (
                <DetailRow label="Koordinatlar" value={`${business.latitude}, ${business.longitude}`} />
              )}
              {business.hours && <DetailRow label="İş saatları" value={business.hours} />}
            </div>

            {/* Nümayəndə */}
            <div>
              <SectionHead>👤 Nümayəndə</SectionHead>
              <DetailRow label="Ad Soyad" value={reg?.representative_name} />
              <DetailRow label="Vəzifə" value={reg?.representative_position} />
              <DetailRow label="Telefon" value={reg?.representative_phone} />
              <DetailRow label="E-poçt" value={reg?.representative_email} />

              <SectionHead style={{ marginTop: 12 }}>🏪 Filiallar ({business.branch_count})</SectionHead>
              {business.branches?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {business.branches.map((loc, i) => (
                    <div key={loc.id || i} style={{
                      background: loc.is_main ? '#FFF8F5' : '#FAFAFA',
                      border: `1px solid ${loc.is_main ? '#FF6B3540' : '#E0E0E0'}`,
                      borderRadius: 10, padding: '10px 12px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A2E' }}>
                          {loc.is_main ? '⭐ ' : '📍 '}{loc.name || `Filial ${i + 1}`}
                        </span>
                        {loc.is_main && (
                          <span style={{ fontSize: 10, background: '#FF6B3520', color: '#FF6B35',
                            padding: '1px 7px', borderRadius: 20, fontWeight: 700 }}>Əsas</span>
                        )}
                        {loc.is_open != null && (
                          <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 'auto',
                            color: loc.is_open ? '#2E7D32' : '#C62828',
                            background: loc.is_open ? '#E8F5E9' : '#FFEBEE',
                            padding: '1px 7px', borderRadius: 20 }}>
                            {loc.is_open ? '● Açıq' : '● Bağlı'}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px', fontSize: 12, color: '#555' }}>
                        {loc.address && <span>📍 {loc.address}</span>}
                        {loc.city && <span>🏙 {loc.city}</span>}
                        {loc.phone && <span>📞 {loc.phone}</span>}
                        {loc.hours && <span>🕐 {loc.hours}</span>}
                        {loc.total_capacity > 0 && <span>💺 {loc.total_capacity} nəfər</span>}
                        {loc.rating > 0 && <span>⭐ {loc.rating}</span>}
                        {loc.business_type && <span>🏢 {loc.business_type}</span>}
                        {loc.cuisine && <span>🍽 {loc.cuisine}</span>}
                      </div>
                      {loc.features?.length > 0 && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                          {loc.features.map(f => (
                            <span key={f} style={{ fontSize: 10, background: '#f0f0f0',
                              color: '#666', padding: '1px 7px', borderRadius: 20 }}>{f}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#bbb' }}>Filial əlavə edilməyib</div>
              )}

              {business.features?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>Xüsusiyyətlər</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {business.features.map(f => (
                      <span key={f} style={{ fontSize: 11, background: '#f5f5f5', color: '#555',
                        padding: '2px 8px', borderRadius: 20 }}>{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lisenziya sənədi */}
          {reg?.license_document_url && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #eee' }}>
              <a href={reg.license_document_url} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                  color: '#fff', background: '#FF6B35', padding: '8px 16px',
                  borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                <FaExternalLinkAlt size={11} /> Lisenziya sənədi
              </a>
            </div>
          )}

          {/* Fotolar */}
          {business.photos?.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <SectionHead>🖼 Fotolar ({business.photos.length})</SectionHead>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {business.photos.slice(0, 8).map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt="" style={{ width: 60, height: 60, objectFit: 'cover',
                      borderRadius: 8, border: '1px solid #eee' }} />
                  </a>
                ))}
                {business.photos.length > 8 && (
                  <div style={{ width: 60, height: 60, borderRadius: 8, background: '#f0f0f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, color: '#888', fontWeight: 700 }}>
                    +{business.photos.length - 8}
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ marginTop: 12, fontSize: 11, color: '#bbb' }}>
            ID: {business.id} · Qeydiyyat: {new Date(business.created_at).toLocaleString('az-AZ')}
          </div>
        </div>
      </td>
    </tr>
  );
}

function BusinessTable({ businesses, onEdit, onKillSwitch, onActivate, onViewStats, onDelete }) {
  const [expandedId, setExpandedId] = useState(null);

  const toggle = (id) => setExpandedId(prev => prev === id ? null : id);

  return (
    <div style={styles.tableContainer}>
      <table style={styles.table}>
        <thead>
          <tr style={styles.headerRow}>
            <th style={styles.th}>#</th>
            <th style={styles.th}>Ad</th>
            <th style={styles.th}>Növ</th>
            <th style={styles.th}>Şəhər</th>
            <th style={styles.th}>Filiallar</th>
            <th style={styles.th}>Tier</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Check-ins</th>
            <th style={styles.th}>Əməliyyatlar</th>
          </tr>
        </thead>
        <tbody>
          {businesses.map((business, index) => {
            const tier = TIER_LABEL[business.premium_tier] || TIER_LABEL[0];
            const icon = BTYPE_ICON[business.business_type] || '🏢';
            const isExpanded = expandedId === business.id;

            return (
              <React.Fragment key={business.id}>
                <tr style={{ ...styles.row, background: isExpanded ? '#FFF8F5' : '#fff' }}>
                  <td style={styles.td}>{String(index + 1).padStart(3, '0')}</td>

                  <td style={styles.td}>
                    <div style={{ fontWeight: 700, color: '#1A1A2E' }}>{business.name}</div>
                    <div style={{ fontSize: 11, color: '#999', marginTop: 1 }}>{business.email}</div>
                  </td>

                  <td style={styles.td}>
                    <span>{icon} {business.business_type || '—'}</span>
                  </td>

                  <td style={styles.td}>{business.city}</td>

                  <td style={styles.td}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                      background: business.branch_count > 0 ? '#E8F5E9' : '#f5f5f5',
                      color: business.branch_count > 0 ? '#2E7D32' : '#bbb',
                    }}>
                      🏪 {business.branch_count > 0 ? `${business.branch_count} filial` : 'Yoxdur'}
                    </span>
                  </td>

                  <td style={styles.td}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                      background: tier.bg, color: tier.color,
                    }}>{tier.text}</span>
                  </td>

                  <td style={styles.td}>
                    {business.is_active ? (
                      <span style={styles.statusActive}><FaCheckCircle /> Aktiv</span>
                    ) : (
                      <span style={styles.statusInactive}><FaTimesCircle /> Deaktiv</span>
                    )}
                  </td>

                  <td style={styles.td}>
                    <strong>{business.total_checkins || 0}</strong>
                  </td>

                  <td style={styles.td}>
                    <div style={styles.actions}>
                      {/* Detaylar */}
                      <button onClick={() => toggle(business.id)} style={styles.btnDetail} title="Detaylar">
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                      </button>

                      <button onClick={() => onEdit(business)} style={styles.btnEdit} title="Düzəliş et">
                        <FaEdit />
                      </button>

                      {business.is_active ? (
                        <KillSwitchButton business={business} onKillSwitch={onKillSwitch} />
                      ) : (
                        <button onClick={() => onActivate(business)} style={styles.btnActivate} title="Aktivləşdir">
                          <FaCheckCircle /> Aktivləşdir
                        </button>
                      )}

                      <button onClick={() => onViewStats(business)} style={styles.btnStats} title="Statistika">
                        <FaChartBar />
                      </button>

                      <button onClick={() => onDelete(business)} style={styles.btnDelete} title="Hesabı sil">
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>

                {isExpanded && <ExpandedDetails business={business} />}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {businesses.length === 0 && (
        <div style={styles.noData}>
          <p>Heç bir business tapılmadı</p>
        </div>
      )}
    </div>
  );
}

const styles = {
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    backgroundColor: '#F8F9FA',
  },
  th: {
    padding: '14px 15px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#666',
    textTransform: 'uppercase',
    borderBottom: '2px solid #E0E0E0',
    whiteSpace: 'nowrap',
  },
  row: {
    borderBottom: '1px solid #F0F0F0',
    transition: 'background-color 0.15s',
  },
  td: {
    padding: '13px 15px',
    fontSize: '13px',
    color: '#1A1A2E',
    verticalAlign: 'middle',
  },
  statusActive: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  statusInactive: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    backgroundColor: '#FFEBEE',
    color: '#C62828',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  actions: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  btnDetail: {
    padding: '7px 10px',
    backgroundColor: '#F0F0F0',
    color: '#555',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s',
  },
  btnEdit: {
    padding: '7px 10px',
    backgroundColor: '#2196F3',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s',
  },
  btnActivate: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '7px 10px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
  },
  btnStats: {
    padding: '7px 10px',
    backgroundColor: '#9C27B0',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  btnDelete: {
    padding: '7px 10px',
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  noData: {
    padding: '40px',
    textAlign: 'center',
    color: '#999',
  },
};

export default BusinessTable;
